<script setup lang="ts">
/**
 * Share Certificate (B32013 · PRS014) — SG 공통 프리셋 **스튜디오** 패널(문서 재현).
 *   기존 ShareCertificateFormModel 을 단일 원본으로 두고 문서 칸과 동기화(useNoticeStudioSync) →
 *   TASK_DATA / L_TASK_EFILING_DATA / 문서 저장 / Edit 스냅샷({formModel}) 은 기존 패널과 같은 형식.
 *   · 회사 선택 → 회사명·UEN·등록사무소(entity-snapshot)
 *   · 문서의 Member ✨ → 챗봇 멤버 선택 → 그 멤버의 증서 데이터(cert no·주식수·클래스·발행일) 스냅샷 로드
 *   · [[Signers]] = 문서 안 SignersField(기본: 이사 + 비서, 기존 buildShareCertificateSignersJson 과 같은 소스)
 *   · Create 시 필수 칸 검사 없음(스튜디오 공통) — 제목·회사만 확인.
 */
import { computed, onMounted, provide, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { saveEFilingData } from '@/features/startTask/api/taskApi';
import { getShareCertificateEntitySnapshot, getShareCertificateSnapshot } from '@/features/startTask/api/shareCertificateApi';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, typeOf } from './final-dividends/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from './final-dividends/cdd/agmSignersContext';
import {
  createEmptyShareCertificateForm,
  applyEntityBasicsToShareCertificateForm,
  applySnapshotToShareCertificateForm,
  buildShareCertificateTaskFormData,
  buildShareCertificatePresetPrepareDocuments,
  buildShareCertificateSignersJson,
  toShareCertificateTemplateVariables,
  type ShareCertificateFormModel,
} from '@/features/startTask/preset_task/sg/components';
import { syncSharesWordFields } from '@/features/startTask/preset_task/sg/components/share-certificate/composables/sharesWords';
import { dedupePresetSigners } from '@/features/startTask/preset_task/sg/components/presetSignerDedup';
import ShareCertificatePackageDoc from '@/features/startTask/preset_task/sg/components/share-certificate/studio/ShareCertificatePackageDoc.vue';
import { useNoticeStudioSync } from '@/features/startTask/preset_task/sg/components/controller/notice-studio/useNoticeStudioSync';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  /** Edit(수정) 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
  studio?: boolean;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'request-address-lookup', context: Record<string, any>): void;
  (e: 'progress-change', pct: number): void;
}>();

type F = keyof ShareCertificateFormModel & string;
/** 문서 토큰 ↔ 기존 모델 필드 */
const FIELD_MAP: Array<[string, F]> = [
  ['company_name', 'companyName'],
  ['uen', 'uen'],
  ['registered_office_address', 'registeredOfficeAddress'],
  ['cert_no', 'certNo'],
  ['no_of_shares', 'noOfShares'],
  ['member_name', 'memberName'],
  ['shares_numeral', 'sharesNumeral'],
  ['shares_words', 'sharesWords'],
  ['share_class', 'shareClass'],
  ['issue_date', 'issueDate'],
];

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const formModel = reactive<ShareCertificateFormModel>(createEmptyShareCertificateForm());
const sync = useNoticeStudioSync(fill, formModel, FIELD_MAP);
// 모델이 자동채움·스냅샷·복원으로 바뀌면 문서 칸에 반영
watch(() => FIELD_MAP.map(([, f]) => formModel[f]), () => sync.push(), { immediate: true });
// 주식 수를 고치면 숫자표기·영문표기(shares_numeral / shares_words)도 같이 — 기존 폼과 같은 규칙
watch(() => formModel.noOfShares, () => syncSharesWordFields(formModel));

const creating = ref(false);
const progress = ref('');
const taskConfigSteps = ref<string[]>([]);
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const icpCd = computed(() => String(props.entityData?.icpCd || props.entityData?.entityCd || '').trim());
provide(AGM_ENTITY_CD, icpCd);
const st = (v: unknown) => (v == null ? '' : String(v).trim());

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], emitProgress, { immediate: true });

// ── 서명자 = 이사 + 비서(기존 buildShareCertificateSignersJson 과 같은 소스). 이미 있으면 유지 ──
let signerSeq = 0;
async function seedSigners(cd: string) {
  if (!cd || signersCtx.signersOf('sc_Signers').length) return;
  try {
    const json = await buildShareCertificateSignersJson(cd);
    const list: Array<Record<string, any>> = json ? JSON.parse(json) : [];
    const entries: SignerEntry[] = list.filter((s) => st(s.name)).map((s) => ({
      id: `id-sg-sc-${signerSeq++}`, name: st(s.name),
      title: s.role === 'secretary' ? 'Secretary' : 'Director', role: st(s.role) || 'director',
      email: st(s.email) || undefined, contactCd: st(s.contactCd) || undefined,
      isManual: true, showTitle: true, _signerLayout: 'box',
    } as SignerEntry));
    if (entries.length && !signersCtx.signersOf('sc_Signers').length) signersCtx.setSigners('sc_Signers', entries);
  } catch (e) { console.warn('[ShareCertificate] signers load failed', e); }
}

// ── 회사 선택 → 회사명·UEN·등록사무소 + 서명자 ──
//   Edit 재진입: 저장값(restoreState)이 우선 — 같은 회사로는 다시 채우지 않는다. 다른 회사로 바꾸면 멤버/증서 칸을 비우고 새로.
const restoredFromSaved = ref(false);
// 멤버 증서 스냅샷 로딩 상태(회사 변경 watch 에서도 쓰므로 먼저 선언)
const loadingSnapshot = ref(false);
const loadError = ref('');
let lastEntityCd = '';
function clearMemberCertificateFields() {
  Object.assign(formModel, {
    memberName: '', contactCd: '', certNo: '', shareClass: '', noOfShares: '',
    sharesNumeral: '', sharesWords: '', issueDate: '', shareholderIdx: undefined, shareCapitalIdx: undefined,
  });
}
async function hydrateCompany(cd: string) {
  try {
    const basics = await getShareCertificateEntitySnapshot(cd);
    if (restoredFromSaved.value) return; // 응답 전에 Edit 복원이 끝났으면 저장값 유지
    applyEntityBasicsToShareCertificateForm(formModel, basics);
  } catch (e) { console.warn('[ShareCertificate] entity-snapshot failed:', e); }
}
watch(
  () => props.entityData,
  (ent) => {
    if (!ent) return;
    const cd = icpCd.value;
    if (!cd || cd === lastEntityCd) return;
    const entityChanged = !!lastEntityCd;
    lastEntityCd = cd;
    if (entityChanged) {
      restoredFromSaved.value = false;
      clearMemberCertificateFields();
      signersCtx.clearAll();
      loadError.value = '';
    }
    if (restoredFromSaved.value || (editMode.value && !entityChanged)) return; // Edit — restoreState 가 채움
    applyEntityBasicsToShareCertificateForm(formModel, ent);
    void hydrateCompany(cd);
    void seedSigners(cd);
  },
  { immediate: true, deep: true },
);

// ── Member ✨ → 챗봇 멤버 선택 → 증서 스냅샷 ──
function requestMemberLookup() {
  emit('request-person-lookup', {
    fieldGroup: 'shareCertificateMember', defaultRole: 'SHARE_CERTIFICATE_MEMBER',
    entityCd: icpCd.value, icpCd: icpCd.value,
  });
}
fill.setActivateHandler((key: string) => {
  if (typeOf(key) === 'entity') { emit('change-entity'); return; }
  if (key === 'member_name') requestMemberLookup();
});
fill.setAddressHandler(async (key: string) => {
  if (key !== 'registered_office_address') return;
  let addresses: Array<{ label: string; value: string; type: string }> = [];
  if (icpCd.value) {
    try {
      const profile = await fetchEntityAddresses(icpCd.value);
      addresses = (profile?.addresses || []).map((a) => ({ label: a.label, value: a.value, type: a.type }));
    } catch { /* ignore */ }
  }
  emit('request-address-lookup', {
    fieldGroup: 'registeredOfficeAddress', index: 0, subjectLabel: 'Registered office',
    addresses: addresses.length > 1 ? addresses : [], directInput: addresses.length <= 1,
  });
});
watch(() => props.selectedAddress, (sel) => {
  if (!sel?.value || st(props.activeAddressContext?.fieldGroup) !== 'registeredOfficeAddress') return;
  formModel.registeredOfficeAddress = sel.value;
});

async function loadSnapshot(contactCd: string, shareholderIdx?: number | string) {
  const ec = icpCd.value;
  if (!ec || !contactCd) return;
  loadingSnapshot.value = true;
  loadError.value = '';
  try {
    const snap: any = await getShareCertificateSnapshot(ec, contactCd, shareholderIdx);
    applySnapshotToShareCertificateForm(formModel, snap);
  } catch (e: any) {
    console.warn('[ShareCertificate] snapshot load failed:', e);
    loadError.value = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message
      || 'Failed to load member certificate data for this member.';
  } finally { loadingSnapshot.value = false; }
}
watch(() => props.selectedPerson, (person) => {
  if (!person) return;
  const group = st(props.activePersonContext?.fieldGroup);
  if (group && group !== 'shareCertificateMember') return;
  const cd = st(person.contactCd);
  // 챗봇 증서 목록에서 고른 경우 그 증서(shareholderIdx). 없으면 그 주주의 최신 증서.
  const shIdx = (person as any).shareholderIdx;
  if (cd && icpCd.value) { void loadSnapshot(cd, shIdx); return; }
  const name = st(person.name || (person as any).contactName);
  if (name) { formModel.memberName = name; formModel.contactCd = cd; }
});

onMounted(async () => {
  emitProgress();
  try {
    const code = props.selection.task.TASK_CODE;
    if (code) { const cfg = await getTaskConfigByCode(code); taskConfigSteps.value = cfg?.processSteps || []; }
  } catch (err) { console.warn('[ShareCertificate] task config fetch failed:', err); }
});

// ── Edit 지원 — 기존 패널 스냅샷 {formModel} + 서명자 ──
function gatherState(): Record<string, any> {
  return { formModel: JSON.parse(JSON.stringify(formModel)), signers: signersCtx.exportAll() };
}
function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  restoredFromSaved.value = true;
  if (s.formModel && typeof s.formModel === 'object') Object.assign(formModel, JSON.parse(JSON.stringify(s.formModel)));
  sync.push();
  if (s.signers) signersCtx.importAll(s.signers);
  // 이전(비스튜디오) 패널로 만든 task 는 서명자 스냅샷이 없다 → 이사·비서로 채움.
  void seedSigners(icpCd.value);
  emitProgress();
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });

/** 문서 서명자 → 기존 signers JSON 형식. 비어 있으면 이사+비서(기존 동작). */
async function resolveSignersJson(): Promise<string> {
  const list = signersCtx.signersOf('sc_Signers').filter((s) => st(s.name));
  if (!list.length) return buildShareCertificateSignersJson(icpCd.value || '');
  const out = dedupePresetSigners(list.map((s) => ({
    name: st(s.name), email: st(s.email), contactCd: st((s as any).contactCd), role: st(s.role) || 'director',
  })));
  return out.length ? JSON.stringify(out) : '';
}

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) { progress.value = 'Error: enter a Task Title in the chat first.'; return; }
  if (!props.entityData) { progress.value = 'Error: select an entity in the chat first.'; return; }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) { progress.value = 'Error: Task code missing on preset.'; return; }
  // 문서 필수 칸 검사는 하지 않는다(스튜디오 공통) — 빈 칸은 문서 단계에서 보완.

  creating.value = true;
  progress.value = editMode.value ? 'Updating task...' : 'Creating task...';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: false,
    };
    formModel.signersJson = await resolveSignersJson();
    const taskFormData: Record<string, any> = buildShareCertificateTaskFormData(formModel, icpCd.value || '');
    taskFormData.presetForm = gatherState();

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskData: { formData: taskFormData } }) });
      try { await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskTitle: props.taskTitle }) }); } catch { /* optional */ }
    } else {
      const taskJson = await fetch('/api/startTask/tasks', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ entityCd: icpCd.value || null, taskTypeCd, taskTypeName: props.selection.task.TASK_NAME, taskTitle: title, taskFlow, officerData: taskFormData }),
      }).then((r) => r.json());
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) { progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`; return; }
    }

    progress.value = 'Saving task data...';
    try {
      await saveEFilingData(taskId, {
        taskTypeCd, entityCd: icpCd.value || null, countryCd: 'SG',
        formData: toShareCertificateTemplateVariables(formModel) as Record<string, unknown>,
        requestPayload: null, responseData: null, status: 'B36002', isSuccess: false,
      });
    } catch (e) { console.warn('[ShareCertificate] L_TASK_EFILING_DATA save failed', e); }

    progress.value = 'Saving documents...';
    const docsToSave = buildShareCertificatePresetPrepareDocuments({
      selectionDocuments: (props.selection.documents || []).map((d: any) => ({ tmpId: d.tmpId, templateName: d.templateName, docCode: d.docCode || d.DOC_TYPE || d.docType })),
      model: formModel,
      personName: formModel.memberName || formModel.companyName || title,
      entityCd: icpCd.value || undefined,
    });
    if (docsToSave.length) {
      try {
        const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((x) => x.json());
        if (!r?.success) progress.value = `Documents save warning: ${r?.message || 'unknown'}`;
      } catch (err) { console.warn('[ShareCertificate] prepare-documents failed', err); }
    } else {
      progress.value = 'Warning: no Share Certificate (PRS014) template linked to this preset.';
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    console.error('[ShareCertificate] create task failed:', err);
    progress.value = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally { creating.value = false; }
}
</script>

<template>
  <div class="sc-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Share Certificate' }}</div>
        <div class="co-subtitle">Share Certificate (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <v-progress-linear v-if="loadingSnapshot" indeterminate color="primary" height="2" />
    <div class="co-doc">
      <ShareCertificatePackageDoc :loading="loadingSnapshot" :load-error="loadError" />
    </div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!entityData || !effectiveTaskTitle || creating" @click="handleCreateTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.sc-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sc-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
